import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import PropTypes from "prop-types";

import { Eye, Search } from "@assets/icons";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./Input.module.scss";

// Add new icon to this object
const icons = {
  eye: Eye,
  search: Search,
};

const Icon = ({ icon, ...props }) => {
  const ViewIcon = icons[icon];
  return <ViewIcon {...props} />;
};

const Input = ({
  className = "",
  innerClassName = "",
  type = "text",
  icon = null,
  iconPosition = "right",
  error = "",
  help = "",
  messageClassName = "",
  messagePosition = "absolute",
  onChange = () => {},
  variant = "",
  placeholder = "",
  isDisabled = false,
  ...props
}) => {
  const [visible, setVisible] = useState(type === "text");
  const inputIcon = icon || (type === "password" ? "eye" : null);
  const { t } = useTranslation();
  const { value, ...inputProps } = props;
  const hasMessage = Boolean(error || help);

  return (
    <div
      className={clsx(
        className,
        "rounded relative",
        isDisabled && "cursor-not-allowed",
      )}
    >
      <div
        className={clsx(
          variant ? styles[variant] : styles.input,
          "border relative border-solid",
          innerClassName,
          // Both classes set `border-color` — applying them together left the
          // outcome dependent on stylesheet order, which silently favored
          // "transparent" and hid the error border entirely. Only one can
          // apply at a time now.
          error ? "border-light-red" : "border-transparent",
          isDisabled ? "bg-silver-light" : "bg-white",
        )}
      >
        {inputIcon && iconPosition === "left" && (
          <div
            onClick={() => {
              if (icon === "eye" || type === "password") setVisible(!visible);
            }}
            className={clsx(
              "absolute h-full flex items-center justify-center transform -translate-y-2/4 left-1.5 inset-y-2/4 w-12 h-full",
              icon === "eye" || type === "password"
                ? visible
                  ? "cursor-pointer"
                  : "opacity-25 cursor-pointer"
                : "",
              isDisabled && "cursor-not-allowed",
            )}
          >
            <Icon icon={inputIcon} />
          </div>
        )}
        <input
          type={visible ? "text" : "password"}
          placeholder={t(placeholder)}
          onChange={(e) => onChange(e.target.value)}
          value={value === null ? "" : value}
          {...inputProps}
          disabled={isDisabled}
          className={
            variant ? styles[`${variant}-field`] : styles["input-field"]
          }
          style={{
            paddingLeft: iconPosition === "left" ? "3rem" : "1rem",
            height: "100%",
            cursor: isDisabled ? "not-allowed" : "text",
            opacity: isDisabled ? 0.5 : 1,
          }}
        />
        {inputIcon && iconPosition === "right" && (
          <div
            onClick={() => {
              if (icon === "eye" || type === "password") setVisible(!visible);
            }}
            className={clsx(
              "absolute flex items-center justify-center transform -translate-y-2/4 right-0 inset-y-2/4 w-12 h-full font-light-poppins",
              icon === "eye" || type === "password"
                ? visible
                  ? "cursor-pointer"
                  : "opacity-25 cursor-pointer"
                : "",
              isDisabled && "cursor-not-allowed",
            )}
          >
            <Icon icon={inputIcon} />
          </div>
        )}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {hasMessage && (
          <motion.div
            key={error || help}
            animate={{ opacity: 1, x: 0, height: "auto" }}
            initial={{ opacity: 0, x: 10, height: 0 }}
            exit={{ opacity: 0, x: -10, height: 0 }}
            transition={{ duration: 0.2 }}
            className={clsx(
              messagePosition === "static" ? "mt-1" : "absolute",
              messageClassName,
            )}
          >
            {error ? (
              <span className="text-light-red text-sm font-light-poppins">
                {t(error)}
              </span>
            ) : (
              <span className="text-dawn-gray font-light-poppins text-sm">
                {t(help)}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

Input.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.oneOf(["eye", "search"]),
  iconPosition: PropTypes.oneOf(["left", "right"]),
  type: PropTypes.oneOf(["text", "password"]),
  error: PropTypes.string,
  onChange: PropTypes.func,
  help: PropTypes.string,
  messageClassName: PropTypes.string,
  messagePosition: PropTypes.oneOf(["absolute", "static"]),
  variant: PropTypes.string,
};

export default Input;
